import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-zinc-500 mb-4">Invalid or missing reset token.</p>
          <Link to="/forgot-password"><Button variant="outline" className="border-[#E6E6E6]">Request New Link</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
            <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
        </div>

        {done ? (
          <div className="text-center">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={24} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit' }}>Password Reset!</h1>
            <p className="text-sm text-zinc-500 mb-6">You can now sign in with your new password.</p>
            <Link to="/login"><Button className="bg-[#F85037] hover:bg-[#e04530] text-white">Go to Login</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit' }}>Set New Password</h1>
            <p className="text-sm text-zinc-500 mb-8">Enter your new password below.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-700">New Password</Label>
                <div className="relative mt-1.5">
                  <Input type={showPw ? "text" : "password"} placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="border-[#E6E6E6] pr-10" data-testid="reset-password-input" required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-zinc-700">Confirm Password</Label>
                <Input type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="reset-confirm-input" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#F85037] hover:bg-[#e04530] text-white h-10" data-testid="reset-submit-btn">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
